import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import PriceTiersModal from "../components/PriceTiersModal";

const ViewProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [selectedVideo, setSelectedVideo] = useState(false);
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [quantity, setQuantity] = useState("");
  const [customQuantity, setCustomQuantity] = useState("100");
  const [selectedImage, setSelectedImage] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const [dieShapes, setDieShapes] = useState([]);
  const [selectedDieShape, setSelectedDieShape] = useState("");
  const [selectedPaperType, setSelectedPaperType] = useState("");
  const [selectedFlapOpening, setSelectedFlapOpening] = useState("");
  const [selectedWindowCutting, setSelectedWindowCutting] = useState("");

  const [selectedPrinting, setSelectedPrinting] = useState("");
  const [selectedLaminationType, setSelectedLaminationType] = useState("");

  const dieShapeImageUrl =
    "https://ik.imagekit.io/7uve7qsipm/images_product-images_159_DIE%20SHAPES%20ALL.jpg?updatedAt=1729937733711";

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(
          `https://api.vyapakprint.com/api/products/getProductById/${id}`,
        );

        const productData = response.data;
        setProduct(productData);
        setSelectedImage(productData?.images?.[0] || null);
      } catch (err) {
        setError("Failed to fetch product data.");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  useEffect(() => {
    const fetchDieShapes = async () => {
      try {
        const response = await axios.get(
          "https://api.vyapakprint.com/api/dieshapes",
        );
        setDieShapes(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Failed to fetch die shapes.", err);
      }
    };

    fetchDieShapes();
  }, []);

  const extractYouTubeID = (url) => {
    if (!url) return null;
    const regExp =
      /^.*(youtu.be\/|v\/|embed\/|watch\?v=|watch\?.+&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const handleUploadDesign = () => {
    navigate("/upload-design");
  };

  const handleCustomizeDesign = () => {
    navigate("/customize-design");
  };

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleOpenImageModal = () => setIsImageModalOpen(true);
  const handleCloseImageModal = () => setIsImageModalOpen(false);

  const handleQuantityChange = (e) => {
    const selectedQty = Number(e.target.value);
    setQuantity(selectedQty);
    setCustomQuantity(String(selectedQty));
  };

  const handleCustomQuantityChange = (e) => {
    const qty = Number(e.target.value);
    if (qty >= 1) {
      setQuantity(qty);
      setCustomQuantity(String(qty));
    }
  };

  const getPrice = (qty) => {
    if (
      !product ||
      !Array.isArray(product.priceTiers) ||
      product.priceTiers.length === 0
    ) {
      return 0;
    }

    const tiers = [...product.priceTiers].sort(
      (a, b) => a.quantity - b.quantity,
    );

    for (let i = tiers.length - 1; i >= 0; i--) {
      if (qty >= tiers[i].quantity) {
        return Number(tiers[i].price) || 0;
      }
    }

    return Number(tiers[0]?.price) || 0;
  };

  const normalizedPrintings = useMemo(() => {
    if (!Array.isArray(product?.printings)) return [];

    return product.printings.map((item) => {
      if (typeof item === "string") {
        return { name: item, price: 0 };
      }

      return {
        name: item?.name || "",
        price: Number(item?.price || 0),
      };
    });
  }, [product]);

  const normalizedLaminationTypes = useMemo(() => {
    if (!Array.isArray(product?.laminationTypes)) return [];

    return product.laminationTypes.map((item) => {
      if (typeof item === "string") {
        return { name: item, price: 0 };
      }

      return {
        name: item?.name || "",
        price: Number(item?.price || 0),
      };
    });
  }, [product]);

  const selectedPrintingData = useMemo(() => {
    return (
      normalizedPrintings.find((item) => item.name === selectedPrinting) || null
    );
  }, [normalizedPrintings, selectedPrinting]);

  const selectedLaminationData = useMemo(() => {
    return (
      normalizedLaminationTypes.find(
        (item) => item.name === selectedLaminationType,
      ) || null
    );
  }, [normalizedLaminationTypes, selectedLaminationType]);

  const baseUnitPrice = getPrice(Number(quantity) || 0);
  const printingExtra = Number(selectedPrintingData?.price || 0);
  const laminationExtra = Number(selectedLaminationData?.price || 0);
  const finalUnitPrice = baseUnitPrice + printingExtra + laminationExtra;
  const finalTotalPrice = Number(quantity || 0) * finalUnitPrice;

  const handleAddToCart = () => {
    if (!quantity) {
      alert("Quantity is required.");
      return;
    }

    if (
      (product.isDieShape && !selectedDieShape) ||
      (product.isPaperType && !selectedPaperType) ||
      (product.isFlapOpening && !selectedFlapOpening) ||
      (product.isWindowCutting && !selectedWindowCutting) ||
      (product.isPrinting && !selectedPrinting) ||
      (product.isLaminationType && !selectedLaminationType)
    ) {
      const missingFields = [];

      if (product.isDieShape && !selectedDieShape)
        missingFields.push("Die Shape");
      if (product.isPaperType && !selectedPaperType)
        missingFields.push("Paper Type");
      if (product.isFlapOpening && !selectedFlapOpening)
        missingFields.push("Flap Opening");
      if (product.isWindowCutting && !selectedWindowCutting)
        missingFields.push("Window Cutting");
      if (product.isPrinting && !selectedPrinting)
        missingFields.push("Printing");
      if (product.isLaminationType && !selectedLaminationType) {
        missingFields.push("Lamination Type");
      }

      alert(`Please select the following fields: ${missingFields.join(", ")}`);
      return;
    }

    const uniqueId = uuidv4();

    const cartItem = {
      id: uniqueId,
      productId: id,
      productName: product.name,
      productImage: selectedImage,
      quantity: Number(quantity),
      basePrice: baseUnitPrice,
      printingPrice: printingExtra,
      laminationPrice: laminationExtra,
      price: finalUnitPrice,
      totalPrice: finalTotalPrice.toFixed(2),
      dieShape: selectedDieShape,
      paperType: selectedPaperType,
      flapOpening: selectedFlapOpening,
      windowCutting: selectedWindowCutting,
      printing: selectedPrinting,
      laminationType: selectedLaminationType,
    };

    const currentCart = JSON.parse(localStorage.getItem("cart")) || [];
    currentCart.push(cartItem);
    localStorage.setItem("cart", JSON.stringify(currentCart));

    alert("Product added to cart!");
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (!product) {
    return <div>No product data available.</div>;
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="md:w-1/2 flex flex-col">
          <div className="mb-4">
            {selectedVideo && product.videoUrl ? (
              <iframe
                width="100%"
                height="350"
                src={`https://www.youtube.com/embed/${extractYouTubeID(product.videoUrl)}`}
                title="Product Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="rounded-lg shadow-md"
              />
            ) : (
              <img
                src={selectedImage || product?.images?.[0]}
                alt="Product"
                className="w-full h-80 object-cover rounded-lg shadow-md"
              />
            )}
          </div>

          <div className="flex overflow-x-auto space-x-2">
            {product.videoUrl && (
              <div
                className="flex-shrink-0 w-24 h-24 cursor-pointer"
                onClick={() => setSelectedVideo(true)}
              >
                <img
                  src={`https://img.youtube.com/vi/${extractYouTubeID(product.videoUrl)}/hqdefault.jpg`}
                  alt="Video Thumbnail"
                  className="w-full h-full object-cover border rounded-lg shadow-md"
                />
              </div>
            )}

            {(product.images || []).map((image, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-24 h-24 cursor-pointer"
                onClick={() => {
                  setSelectedImage(image);
                  setSelectedVideo(false);
                }}
              >
                <img
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover border rounded-lg shadow-md"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="md:w-1/2 md:pl-6">
          <h1 className="text-3xl font-bold mb-2">{product.name}</h1>

          <div className="mt-2">
            <p className="text-gray-700">
              {String(product.description || "")
                .split(",")
                .map((item, index) => (
                  <span
                    key={index}
                    className={`block ${index > 0 ? "mt-2" : ""}`}
                  >
                    {index === 0 && (
                      <i className="fa-solid fa-circle-info mr-2"></i>
                    )}
                    {item.trim()}
                  </span>
                ))}
            </p>
          </div>

          <h1 className="my-3 font-semibold text-mainpink">
            <i className="fa-solid fa-truck"></i> Delivery Estimate :{" "}
            {product.expectedDeliveryTime}
          </h1>

          <div className="mb-4">
            {product.isDieShape && (
              <>
                <h2 className="text-xl font-semibold my-2">Select Die Shape</h2>
                <select
                  value={selectedDieShape}
                  onChange={(e) => setSelectedDieShape(e.target.value)}
                  className="border rounded-lg py-2 px-4 w-full"
                  required
                >
                  <option value="">Select Die Shape</option>
                  {dieShapes.map((shape) => (
                    <option key={shape._id} value={shape.name}>
                      {shape.name}
                    </option>
                  ))}
                </select>

                <div className="mb-4">
                  <button
                    onClick={handleOpenImageModal}
                    className="bg-mainblue z-[99] text-white py-2 px-4 rounded-lg hover:bg-blue-700 my-2"
                  >
                    See Die Shapes Options
                  </button>

                  {isImageModalOpen && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 backdrop-blur-sm flex justify-center items-center z-50">
                      <div className="bg-white rounded-lg p-6 shadow-lg max-w-6xl w-full">
                        <h3 className="text-lg font-semibold mb-4 text-center">
                          Die Shape Image
                        </h3>

                        <img
                          src={dieShapeImageUrl}
                          alt="Die Shape"
                          className="mb-4 mx-auto object-contain"
                        />

                        <button
                          onClick={handleCloseImageModal}
                          className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-red-400"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            <h2 className="text-xl font-semibold mb-2">
              Select {product.isPaperType ? "Details" : "Quantity"}
            </h2>

            {product.isPaperType && (
              <div className="mb-4">
                <select
                  value={selectedPaperType}
                  onChange={(e) => setSelectedPaperType(e.target.value)}
                  className="border rounded-lg py-2 px-4 w-full"
                >
                  <option value="">Select Paper Type</option>
                  {(product.paperTypes || []).map((type, index) => (
                    <option
                      key={index}
                      value={typeof type === "string" ? type : type?.name}
                    >
                      {typeof type === "string" ? type : type?.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {product.isFlapOpening && (
              <div className="mb-4">
                <select
                  value={selectedFlapOpening}
                  onChange={(e) => setSelectedFlapOpening(e.target.value)}
                  className="border rounded-lg py-2 px-4 w-full"
                >
                  <option value="">Select Flap Opening</option>
                  {(product.flapOpenings || []).map((opening, index) => (
                    <option
                      key={index}
                      value={
                        typeof opening === "string" ? opening : opening?.name
                      }
                    >
                      {typeof opening === "string" ? opening : opening?.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {product.isWindowCutting && (
              <div className="mb-4">
                <select
                  value={selectedWindowCutting}
                  onChange={(e) => setSelectedWindowCutting(e.target.value)}
                  className="border rounded-lg py-2 px-4 w-full"
                >
                  <option value="">Select Window Cutting</option>
                  {(product.windowCuttings || []).map((cutting, index) => (
                    <option
                      key={index}
                      value={
                        typeof cutting === "string" ? cutting : cutting?.name
                      }
                    >
                      {typeof cutting === "string" ? cutting : cutting?.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {product.isPrinting && (
              <div className="mb-4">
                <select
                  value={selectedPrinting}
                  onChange={(e) => setSelectedPrinting(e.target.value)}
                  className="border rounded-lg py-2 px-4 w-full"
                >
                  <option value="">Select Printing</option>
                  {normalizedPrintings.map((printing, index) => (
                    <option key={index} value={printing.name}>
                      {printing.name}
                      {printing.price > 0 ? ` (+₹${printing.price}/print)` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {product.isLaminationType && (
              <div className="mb-4">
                <select
                  value={selectedLaminationType}
                  onChange={(e) => setSelectedLaminationType(e.target.value)}
                  className="border rounded-lg py-2 px-4 w-full"
                >
                  <option value="">Select Lamination Type</option>
                  {normalizedLaminationTypes.map((lamination, index) => (
                    <option key={index} value={lamination.name}>
                      {lamination.name}
                      {lamination.price > 0
                        ? ` (+₹${lamination.price}/print)`
                        : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="mb-4">
            <select
              value={quantity}
              onChange={handleQuantityChange}
              className="border rounded-lg py-2 px-4 w-full"
            >
              <option value="">Select Quantity</option>
              {(product.priceTiers || []).map((tier, index) => (
                <option key={index} value={tier.quantity}>
                  {tier.quantity}
                </option>
              ))}
            </select>

            <p className="mt-2 text-lg font-bold">
              Base Price: ₹{baseUnitPrice} per print
            </p>

            {selectedPrintingData && (
              <p className="text-sm text-gray-600">
                Printing Extra: ₹{printingExtra} per print
              </p>
            )}

            {selectedLaminationData && (
              <p className="text-sm text-gray-600">
                Lamination Extra: ₹{laminationExtra} per print
              </p>
            )}

            <p className="mt-1 text-lg font-bold">
              Final Price: ₹{finalUnitPrice} per print
            </p>

            <p className="text-gray-500">
              Total Price: ₹{finalTotalPrice.toFixed(2)}
            </p>

            <div className="mt-4 bg-red-100 p-4 rounded-lg text-gray-700 text-sm">
              <p>
                <span className="font-bold">Designing Free:</span> After placing
                the order, our executive will contact you on WhatsApp regarding
                your designing needs.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-4">
            <button
              onClick={handleOpenModal}
              className="bg-pink-500 text-white py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors"
            >
              What We Charge
            </button>

            <button
              onClick={handleUploadDesign}
              className="bg-pink-500 text-white py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors"
            >
              Upload Design
            </button>

            <button
              onClick={handleCustomizeDesign}
              className="bg-pink-500 text-white py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors"
            >
              Customize Design
            </button>
          </div>

          <div className="w-full">
            <button
              onClick={handleAddToCart}
              className="w-full bg-blue-500 text-white py-3 px-4 rounded-lg hover:bg-blue-600 font-medium text-lg transition-colors"
            >
              Add to Cart
            </button>
          </div>

          <PriceTiersModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            priceTiers={product.priceTiers || []}
          />
        </div>
      </div>
    </div>
  );
};

export default ViewProduct;
